const SCHEME_REGEXP = /^[a-z][a-z0-9+.-]*:\/\//;
const WWW_PREFIX_REGEXP = /^www\./;
const TRAILING_DOTS_REGEXP = /\.+$/;

/*
  Reduces user input to the bare host a company is identified by, because domains are matched
  against the domain of an email address. A `www.` prefix, a scheme or a path can never be part
  of an email domain, so they are stripped instead of being persisted as an entry that can never
  match. Deeper hosts such as `mail.company.com` or `firma.co.uk` are left untouched, as those
  are valid email domains.
*/
export function normalizeDomain(value?: string | null): string | undefined {
  if (!value) return undefined;

  const host = value
    .trim()
    .toLowerCase()
    .replace(SCHEME_REGEXP, "")
    .split("/")[0]
    .split("?")[0]
    .split("#")[0]
    .split("@")
    .pop();

  const domain = (host ?? "").split(":")[0].replace(WWW_PREFIX_REGEXP, "").replace(TRAILING_DOTS_REGEXP, "");

  return domain || undefined;
}

/*
  Normalizes a list of domains, dropping empty entries and duplicates that only differed
  by casing, a `www.` prefix or surrounding whitespace.
*/
export function normalizeDomains(values?: (string | undefined | null)[] | null): string[] {
  const domains = new Set<string>();
  for (const value of values ?? []) {
    const domain = normalizeDomain(value);
    if (domain) domains.add(domain);
  }
  return [...domains];
}

export function extractDomainFromEmail(email?: string | null): string | undefined {
  if (!email) return undefined;
  const at = email.lastIndexOf("@");
  if (at < 0) return undefined;
  const domain = email
    .slice(at + 1)
    .trim()
    .toLowerCase();
  return domain || undefined;
}

export function uniqueDomainsFromEmails(emails: (string | undefined | null)[]): string[] {
  const domains = new Set<string>();
  for (const email of emails) {
    const domain = extractDomainFromEmail(email);
    if (domain) domains.add(domain);
  }
  return [...domains];
}
